import { useUser } from "@features/user/use-user";
import { XMarkIcon } from "@heroicons/react/16/solid";
import { IconButton, Text, Theme } from "@radix-ui/themes";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import posthog from "posthog-js";
import { LoginForm } from "./login-form";

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const LoginModal = ({ isOpen, onClose, onSuccess }: LoginModalProps) => {
  const { user } = useUser();
  const { t } = useTranslation();
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // Handle successful login
  useEffect(() => {
    if (user.data && isOpen) {
      posthog.capture("login_modal_success");
      onSuccess?.();
    }
  }, [user.data, isOpen, onSuccess]);

  useEffect(() => {
    if (isOpen) {
      document.location =
        "/login?return_to=" + encodeURIComponent(window.location.href);
    }
  }, [isOpen]);

  const handleLoginStart = () => {
    setIsLoggingIn(true);
    posthog.capture("login_modal_attempt");
  };

  const handleLoginEnd = () => {
    setIsLoggingIn(false);
  };

  const portalRoot = document.querySelector("#root > div");
  if (!portalRoot) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <Theme
          accentColor="indigo"
          grayColor="slate"
          radius="full"
          scaling="100%"
          appearance="inherit"
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 flex items-center justify-center"
            style={{ zIndex: 1100 }}
          >
            {/* Background overlay */}
            <div className="absolute inset-0 bg-black/60" />

            {/* Modal content */}
            <motion.div
              initial={{ y: 20, opacity: 0, scale: 0.95 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 20, opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              className="relative bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <IconButton
                variant="ghost"
                color="gray"
                className="absolute top-4 right-4 z-10"
                onClick={onClose}
              >
                <XMarkIcon className="w-5 h-5" />
              </IconButton>

              {/* Content */}
              <div className="p-8 pt-12">
                <LoginForm
                  onLoginStart={handleLoginStart}
                  onLoginEnd={handleLoginEnd}
                  isFromUpload={true}
                  compact={true}
                />
              </div>

              {/* Loading overlay */}
              {isLoggingIn && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="absolute inset-0 bg-white/80 dark:bg-slate-900/80 flex items-center justify-center"
                >
                  <div className="flex flex-col items-center space-y-3">
                    <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                    <Text size="2" className="text-gray-500">
                      {t("login.signing_in", "Signing in...")}
                    </Text>
                  </div>
                </motion.div>
              )}
            </motion.div>
          </motion.div>
        </Theme>
      )}
    </AnimatePresence>,
    portalRoot,
  );
};
